import { MutableRefObject, useRef } from 'react';
import {
  AnnotationCords,
  AnnotationDefinitionCore,
  AnnotationOnImage,
  AnnotationType,
} from '../../models/annotation.types';
import { AnnotationImageData, pageImageData } from './Preview.types';
import {
  convertToGrayScale,
  getPointRelativeToImage,
  getTargetPageImage,
  makeImageLocked,
  makeImageMovable,
  translatePagePositionToCanvasCoordinates,
} from './previewUtils';
import {
  createAnnotationImage,
  createNewAnnotationPositionBasedOnImage,
  getActiveAndInactiveAnnotations,
  getAnnotationDefinitionCore,
  getCurrentAnnotationScale,
  getNewHighestLayerNumber,
  getOpacityForAnnotation,
  isAnnotationPositionChanged,
  setDynamicAnnotationScale,
  setImageCoords,
  setupDynamicAnnotation,
} from './annotationsOnPageManagerService';
import { MouseButtons } from '../../const/mouse';
import { fabric } from 'fabric';
import useLongClick from '../../hooks/useLongClick';
import { useAnnotations } from './useAnnotations';
import { ANNOTATION_OBJECT_NAME } from './annotationsConsts';
import useSessionUser from '../../hooks/useSessionUser';
import { ExamScoringSchema } from '../../models/assessment.types';

type onAnnotationRightClickCallback = (
  event: fabric.IEvent,
  annotation: AnnotationOnImage,
  image: fabric.Object,
) => void;
type onStoreAnnotationCallback = (updatedAnnotations: AnnotationOnImage, isDynamic: boolean) => void;

export interface UseAnnotationImageSetupProps {
  onAnnotationRightClick: onAnnotationRightClickCallback | null;
  onStoreAnnotation: onStoreAnnotationCallback | null;
  isAnnotationActive: boolean;
}

export const useAnnotationsImageSetup = ({
  onAnnotationRightClick,
  onStoreAnnotation,
}: UseAnnotationImageSetupProps) => {
  const lastValidAnnotationCoords = useRef<AnnotationCords | null>(null);
  const { user } = useSessionUser();
  const { beginLongClick, cancelLongClick } = useLongClick();

  const {
    canvas,
    annotations,
    activeCriterion,
    activeTask,
    addingAnnotationEnabled,
    annotationVersions,
    annotationDefinitions,
    pageImages,
    lastAddedAnnotationId,
    canEditRemoveAnnotation,
    rotateAngle,
    currentCoords,
    setLastAddedAnnotationId,
    canUserEditRemoveAnnotation,
    cursorDimensions,
    pngCache,
    svgCache,
    getCanvasPointsForAnnotation,
    activeAnnotationId,
    examinerFunction,
    isActiveAnnotationForGrading,
    activeAnnotationsRef,
    addingModeRef,
  } = useAnnotations();

  const normalizeImageLocation = (image: fabric.Object) => {
    if (image.left !== undefined && image.top !== undefined && image.height !== undefined) {
      const targetImageData: pageImageData | undefined = getTargetPageImage(pageImages, image.left, image.top, -5);

      if (targetImageData) {
        lastValidAnnotationCoords.current = { left: image.left, top: image.top };
      } else if (lastValidAnnotationCoords.current) {
        image.left = lastValidAnnotationCoords.current.left;
        image.top = lastValidAnnotationCoords.current.top;
      }
    }
  };

  const setupAnnotationImage = (
    image: fabric.Object,
    left: number,
    top: number,
    annotation: AnnotationOnImage,
    annotationDefinitionCore: AnnotationDefinitionCore,
    allowMoving?: boolean,
    withEventsCallbacks = true,
  ): void => {
    const isSelectable = Boolean(
      canEditRemoveAnnotation &&
        activeCriterion &&
        annotation.examScoringSchemaId === activeCriterion.examScoringSchemaId &&
        rotateAngle === 0,
    );

    image.set({
      left,
      top,
      originX: 'center',
      originY: 'center',
      hasControls: isSelectable && annotationDefinitionCore.annotationType === AnnotationType.dynamic,
      borderColor: '#3560FF',
      borderOpacityWhenMoving: 1,
      selectable: isSelectable,
      opacity: getOpacityForAnnotation(annotation, activeCriterion),
      lockMovementX: false,
      lockMovementY: false,
      lockSkewingX: true, // blockade against Skewing (| | -> / /) with SHIFT + moving center edge selector
      lockSkewingY: true,
      hasBorders: true,
      cornerColor: 'white',
      cornerStrokeColor: '#3560FF',
      transparentCorners: false,
    });

    if (rotateAngle !== 0 && currentCoords) {
      setImageCoords(annotation, image, annotationDefinitionCore, rotateAngle, currentCoords);
    }

    if (!allowMoving) {
      makeImageLocked(image);
    } else {
      canvas.setActiveObject(image);
      makeImageMovable(image);
    }

    if (!addingAnnotationEnabled || (activeTask && activeTask.noAnswer)) {
      convertToGrayScale(image);
    }
    const defaultScale = getCurrentAnnotationScale();
    image.scale(defaultScale);
    if (annotationDefinitionCore.annotationType === AnnotationType.dynamic) {
      setDynamicAnnotationScale(
        annotationDefinitionCore,
        annotation,
        image,
        defaultScale,
        rotateAngle,
        withEventsCallbacks,
      );
    }

    if (withEventsCallbacks) {
      image.on('mousedown', (event: fabric.IEvent) => {
        setLastAddedAnnotationId(null);
        if (
          !canEditRemoveAnnotation ||
          !activeCriterion ||
          annotation.examScoringSchemaId !== activeCriterion.examScoringSchemaId ||
          rotateAngle !== 0
        ) {
          return;
        }
        if (event.button === MouseButtons.left) {
          event.e.preventDefault();
          event.e.stopPropagation();

          if (addingModeRef.current.isAdding) {
            return;
          }
          if (canEditRemoveAnnotation && canUserEditRemoveAnnotation(annotation)) {
            makeImageMovable(image);
            canvas.renderAll();
            beginLongClick(() => {
              makeImageMovable(image);
              canvas.renderAll();
            });
          }
        } else if (event.button === MouseButtons.right) {
          onAnnotationRightClick?.(event, annotation, image);
        }
      });

      image.on('mouseup', (event) => {
        if (
          !canEditRemoveAnnotation ||
          !activeCriterion ||
          annotation.examScoringSchemaId !== activeCriterion.examScoringSchemaId
        ) {
          return;
        }
        if (event.button === MouseButtons.left) {
          cancelLongClick();
          if (!image.lockMovementX && canEditRemoveAnnotation && canUserEditRemoveAnnotation(annotation)) {
            if (!image.left && !image.top && !image.height) return;

            const updatedAnnotation = createNewAnnotationPositionBasedOnImage(pageImages, image, annotation);
            if (!updatedAnnotation) return;

            if (annotationDefinitionCore.annotationType === AnnotationType.dynamic) {
              setupDynamicAnnotation(updatedAnnotation, annotationDefinitionCore, cursorDimensions.current, image);
            }

            if (isAnnotationPositionChanged(updatedAnnotation, annotation)) {
              const isDynamic = annotationDefinitionCore.annotationType === AnnotationType.dynamic;
              onStoreAnnotation?.(updatedAnnotation, isDynamic);
            }
          }
        }
      });
      image.on('moving', () => normalizeImageLocation(image));
    }
    return;
  };

  const createAnnotation = async (
    annotation: AnnotationOnImage,
    withEventsCallbacks = true,
  ): Promise<AnnotationImageData | null> => {
    const annotationDefinitionCore = getAnnotationDefinitionCore(
      annotation,
      activeTask,
      annotationVersions,
      annotationDefinitions,
    );

    if (!annotationDefinitionCore) return null;

    const annotationCoordinates = translatePagePositionToCanvasCoordinates(
      pageImages,
      annotation.positionX,
      annotation.positionY,
      annotation.pageNumber,
    );

    if (annotationCoordinates) {
      const image = await createAnnotationImage(
        annotation,
        annotationDefinitionCore,
        addingAnnotationEnabled,
        activeTask,
        pngCache.current,
        svgCache.current,
      );
      if (!image) return null;

      await setupAnnotationImage(
        image,
        annotationCoordinates.left,
        annotationCoordinates.top,
        annotation,
        annotationDefinitionCore,
        annotation.annotationId === lastAddedAnnotationId,
        withEventsCallbacks,
      );

      const newAnnotationImageData: AnnotationImageData = { image, annotation, annotationDefinitionCore };
      return newAnnotationImageData;
    }
    return null;
  };

  const loadAndDrawAnnotationsAsync = async (
    activeAnnotationsRef: MutableRefObject<AnnotationImageData[]>,
    annotationsRenderingFlag: MutableRefObject<boolean>,
  ) => {
    const start = Date.now();
    const activeAndInactive = getActiveAndInactiveAnnotations(annotations, activeCriterion);
    activeAnnotationsRef.current = [];

    const annotationsImageData: AnnotationImageData[] = [];
    for (let i = 0; i < activeAndInactive.inactive.length; i++) {
      const newAnnotation = await createAnnotation(activeAndInactive.inactive[i]);
      if (newAnnotation) annotationsImageData.push(newAnnotation);
    }
    for (let i = 0; i < activeAndInactive.active.length; i++) {
      const newAnnotation = await createAnnotation(activeAndInactive.active[i]);
      if (newAnnotation) {
        annotationsImageData.push(newAnnotation);
        activeAnnotationsRef.current.push(newAnnotation);
      }
    }
    canvas.getObjects().forEach((item) => {
      if (item.name?.includes(ANNOTATION_OBJECT_NAME)) {
        canvas.remove(item);
      }
    });
    for (let i = 0; i < annotationsImageData.length; i++) {
      const annotation = annotationsImageData[i];
      canvas.add(annotation.image);
      canvas.bringToFront(annotation.image);
    }
    const end = Date.now();
    console.log('   Pre-render time:', end - start);
    canvas.renderAll();
    const endRender = Date.now();
    console.log('   Render time:', endRender - start, annotationsImageData.length);
    annotationsRenderingFlag.current = false;
  };

  const createNewAnnotation = (
    x: number,
    y: number,
    targetImageData: pageImageData,
    annotationDefinitionCore: AnnotationDefinitionCore,
    criterion: ExamScoringSchema,
    width = 0,
    height = 0,
    angle: number | null = null,
  ): AnnotationOnImage | null => {
    if (
      !targetImageData ||
      !targetImageData.image ||
      targetImageData.image.left === undefined ||
      targetImageData.image.top === undefined
    ) {
      return null;
    }

    const canvasPoints = getCanvasPointsForAnnotation(x, y);
    const coords = getPointRelativeToImage(canvasPoints.x, canvasPoints.y, targetImageData);

    const newAnnotation: AnnotationOnImage = {
      annotationId: '',
      annotationDefinitionId: activeAnnotationId,
      examinerId: user.profile.sub,
      examinerFunctionWithZwo: examinerFunction,
      examScoringSchemaId: criterion.examScoringSchemaId,
      positionX: coords.x,
      positionY: coords.y,
      pageNumber: targetImageData.pageNumber,
      width: Math.round(width) || null,
      height: Math.round(height) || null,
      angle,
      forScoring: criterion.isScoringAnnotationRequired && isActiveAnnotationForGrading,
      layer: getNewHighestLayerNumber(activeAnnotationsRef.current),
    };

    if (!width || !height) {
      if (annotationDefinitionCore.annotationType === AnnotationType.dynamic) {
        setupDynamicAnnotation(newAnnotation, annotationDefinitionCore, cursorDimensions.current);
      }
    }

    return newAnnotation;
  };

  const removeTempAnnotationFromCanvas = () => {
    const foundAnnotations: fabric.Object[] = [];
    if (!canvas) return;
    canvas.getObjects().forEach((annotation) => {
      if (annotation.name === ANNOTATION_OBJECT_NAME) {
        foundAnnotations.push(annotation);
      }
    });

    if (foundAnnotations.length > 0) {
      for (let i = 0; i < foundAnnotations.length; i++) {
        canvas?.remove(foundAnnotations[i]);
      }

      canvas?.renderAll();
    }
  };

  return {
    loadAndDrawAnnotationsAsync,
    createNewAnnotation,
    createAnnotation,
    removeTempAnnotationFromCanvas,
  };
};
